import classNames from 'classnames';
import { Button, Theme } from '@carbon/react';
import { TypeCurveStatusTag } from 'components/lifecycle-status-tag';
import {
  Checkmark,
  CheckmarkFilled,
  CheckmarkOutline,
  CloseFilled,
  CloseOutline,
  Rocket,
  TaskView,
} from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';

import { SidebarToggle } from 'components/layout/dual-sidebar-layout';
import { EventsPanelOpenButton } from 'components/events-panel';

import { tcDetailsRightPanelAtom } from './atoms';
import { type TypeCurveWorkspaceResponse } from 'types/type-curve-workspace';
import {
  tcWorkspaceTasksAtom,
  tcWorkspaceTasksTotalAtom,
  tcWorkspaceTasksCompletedAtom,
  tcRequiredTasksCompleteAtom,
  tcVolumesVerdictExistsAtom,
  tcOverallVerdictSetAtom,
  OVERALL_TYPE_CURVE_KEY,
  useSaveWorkspaceTask,
} from './tasks';

import style from './type-curve-workspace-header.module.scss';
import { WorkspaceHeaderLeading } from './workspace-header-leading';

const NO_PROMOTE_BLOCKERS: string[] = [];

export interface TypeCurveWorkspaceHeaderProps {
  workspace: TypeCurveWorkspaceResponse;
  /** Type curve display id (e.g. uniqueTcId) shown as subtitle */
  typeCurveLabel: string;
  leftPanelOpen: boolean;
  onLeftPanelToggle: (open: boolean) => void;
  onBack: () => void;
  onPromote: () => void;
  /** Server blockers beyond the tasks (e.g. an archived Type Curve Area). Any of them disables
   * Promote, and they become the button's hint. */
  promoteBlockers?: string[];
}

export const TypeCurveWorkspaceHeader = ({
  workspace,
  typeCurveLabel,
  leftPanelOpen,
  onLeftPanelToggle,
  onBack,
  onPromote,
  promoteBlockers = NO_PROMOTE_BLOCKERS,
}: TypeCurveWorkspaceHeaderProps) => {
  const setRightPanel = useSetAtom(tcDetailsRightPanelAtom);
  const rightPanel = useAtomValue(tcDetailsRightPanelAtom);

  // The header "Mark as Correct/Incorrect" buttons are the overall Type Curve
  // verdict: they patch the `type_curve` verification task by its path. The task
  // stays in the atom (read here) but is hidden from the checklist + counts.
  const { tasks } = useAtomValue(tcWorkspaceTasksAtom);
  const typeCurveTask = tasks.find((t) => t.path === OVERALL_TYPE_CURVE_KEY);
  const status = typeCurveTask?.status ?? null;
  const verificationIsCorrect = status === 'correct';
  const verificationIsIncorrect = status === 'incorrect';

  const saveTask = useSaveWorkspaceTask();

  const total = useAtomValue(tcWorkspaceTasksTotalAtom);
  const completed = useAtomValue(tcWorkspaceTasksCompletedAtom);

  // Promotable once every REQUIRED verification task carries a verdict, at least
  // one volumes task (oil/gas/water) has been verdicted, AND the Type Curve
  // itself has been marked Correct or Incorrect from this header.
  const requiredTasksComplete = useAtomValue(tcRequiredTasksCompleteAtom);
  const volumesVerdictExists = useAtomValue(tcVolumesVerdictExistsAtom);
  const overallVerdictSet = useAtomValue(tcOverallVerdictSetAtom);
  const canPromote =
    requiredTasksComplete &&
    volumesVerdictExists &&
    overallVerdictSet &&
    promoteBlockers.length === 0 &&
    !workspace.promoted;
  const promoteHint =
    !workspace.promoted && promoteBlockers.length > 0
      ? promoteBlockers.join('. ')
      : undefined;
  const PromoteIcon = workspace.promoted ? Checkmark : Rocket;
  const tasksPanelOpen = rightPanel.type === 'tasks' && rightPanel.open;

  const markActionsDisabled =
    workspace.promoted || saveTask.isPending || !typeCurveTask;

  // Setting a verdict opens the decision sidebar to capture a reason; the panel's
  // Save patches the overall Type Curve task (path `type_curve`). Clicking the
  // already-active verdict reverts it to unverified directly (no reason needed).
  const onMarkCorrect = () => {
    if (markActionsDisabled) return;
    if (verificationIsCorrect) {
      saveTask.mutate({
        path: OVERALL_TYPE_CURVE_KEY,
        correctnessState: null,
        reason: '',
      });
      return;
    }
    setRightPanel({ type: 'verification', kind: 'correct', open: true });
  };

  const onMarkIncorrect = () => {
    if (markActionsDisabled) return;
    if (verificationIsIncorrect) {
      saveTask.mutate({
        path: OVERALL_TYPE_CURVE_KEY,
        correctnessState: null,
        reason: '',
      });
      return;
    }
    setRightPanel({ type: 'verification', kind: 'incorrect', open: true });
  };

  return (
    <header className={style.shell}>
      <div className={style.row}>
        <WorkspaceHeaderLeading name={workspace.name} onBack={onBack}>
          {/* The header is dark, so the tag takes Carbon's g90 tag tokens. */}
          <Theme theme="g90" as="span" className={style.statusTagZone}>
            <TypeCurveStatusTag
              promoted={workspace.promoted}
              isDefault={workspace.isDefault}
              archived={workspace.archived}
            />
          </Theme>
          <span className={style.typeCurveId}>{typeCurveLabel}</span>
        </WorkspaceHeaderLeading>

        <div className={style.actions}>
          <div className={style.darkToolbarControl}>
            <SidebarToggle
              side="left"
              isOpen={leftPanelOpen}
              onToggle={onLeftPanelToggle}
              label="Panel"
            />
          </div>
          <EventsPanelOpenButton
            className={style.eventsButtonOnDark}
            tooltipPosition="bottom"
          />
          <button
            type="button"
            className={classNames(style.taskButton, {
              [style.taskButtonOpen]: tasksPanelOpen,
            })}
            aria-pressed={tasksPanelOpen}
            onClick={() => {
              setRightPanel((prev) => {
                if (prev.type === 'tasks' && prev.open) {
                  return { type: 'dca', open: false };
                }
                return { type: 'tasks', open: true };
              });
            }}
          >
            <span className={style.taskLabel}>
              Tasks {completed}/{total}
            </span>
            <TaskView size={16} className={style.taskIcon} aria-hidden />
          </button>
          {!workspace.promoted ? (
            <>
              <button
                type="button"
                className={style.markButton}
                disabled={markActionsDisabled}
                onClick={onMarkCorrect}
              >
                <span className={style.markButtonLabel}>Mark as Correct</span>
                {verificationIsCorrect ? (
                  <CheckmarkFilled
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                ) : (
                  <CheckmarkOutline
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                )}
              </button>
              <button
                type="button"
                className={style.markButton}
                disabled={markActionsDisabled}
                onClick={onMarkIncorrect}
              >
                <span className={style.markButtonLabel}>Mark as Incorrect</span>
                {verificationIsIncorrect ? (
                  <CloseFilled
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                ) : (
                  <CloseOutline
                    size={16}
                    className={style.markIcon}
                    aria-hidden
                  />
                )}
              </button>
            </>
          ) : null}
          <Button
            kind="primary"
            size="md"
            disabled={!canPromote}
            title={promoteHint}
            onClick={onPromote}
            className={style.promoteButton}
            renderIcon={(props) => <PromoteIcon size={16} {...props} />}
          >
            {workspace.promoted ? 'Promoted' : 'Promote'}
          </Button>
        </div>
      </div>
    </header>
  );
};
